import { isMinimalHeaderPath } from 'platform/forms-system/src/js/patterns/minimal-header';
import { USER_ENTERED_ADDRESS_ID } from './getAddressValidationSubmitStrategy';

export const PRIMARY_BUTTON_VARIANTS = Object.freeze({
  EDIT: 'edit',
  SUBMIT: 'submit',
});

export const USE_ENTERED_ADDRESS_TEXT = 'Use address you entered';
export const USE_SUGGESTED_ADDRESS_TEXT = 'Use suggested address';

/**
 * Which button the form's primary slot shows, and its label.
 *
 * There is nothing to submit when validation failed outright, or when the API
 * returned neither a suggestion nor an override key — the only way forward is
 * back to the edit form.
 *
 * PORT NOTE: the class opened with `if (confirmedSuggestions.length === 0 &&
 * overrideValidationKey) buttonText = 'Use address you entered'`, re-assigning
 * the value the variable already held. Dropped as dead.
 */
export const getPrimaryButtonConfig = ({
  addressValidationError = false,
  overrideValidationKey,
  confirmedSuggestions = [],
  selectedAddressId,
  isLoading = false,
}) => {
  if (
    addressValidationError ||
    (!confirmedSuggestions.length && !overrideValidationKey)
  ) {
    return { variant: PRIMARY_BUTTON_VARIANTS.EDIT };
  }

  // PORT NOTE: the class gated this on `confirmedSuggestions.length === 1`, so
  // with two or more suggestions the button read 'Use address you entered'
  // however the user had chosen — including on first paint, where the
  // ADDRESS_VALIDATION_CONFIRM action defaults selectedAddressId to '0'. Only
  // the label was wrong; getAddressValidationSubmitStrategy keys
  // suggestedAddressSelected off selectedAddressId alone and submitted the
  // right address throughout. Fixed here to match that definition.
  const usesSuggestion =
    confirmedSuggestions.length > 0 &&
    selectedAddressId !== USER_ENTERED_ADDRESS_ID;

  const buttonText = usesSuggestion
    ? USE_SUGGESTED_ADDRESS_TEXT
    : USE_ENTERED_ADDRESS_TEXT;

  return {
    variant: PRIMARY_BUTTON_VARIANTS.SUBMIT,
    loading: isLoading,
    // The loading spinner replaces the label rather than sitting beside it.
    text: isLoading ? '' : buttonText,
  };
};

/**
 * Whether an address option renders as a selectable radio or as static text,
 * and what its label reads.
 *
 * The user-entered address is only selectable when an override key exists —
 * without one there is nothing authorising it to be saved as typed.
 */
export const getAddressOptionConfig = ({
  id = USER_ENTERED_ADDRESS_ID,
  confirmedSuggestions = [],
  overrideValidationKey,
}) => {
  const isAddressFromUser = id === USER_ENTERED_ADDRESS_ID;

  const hasConfirmedSuggestions =
    (confirmedSuggestions.length > 0 && !!overrideValidationKey) ||
    confirmedSuggestions.length > 1;

  const isFirstOptionOrEnabled =
    (isAddressFromUser && !!overrideValidationKey) || !isAddressFromUser;

  return {
    isAddressFromUser,
    hasConfirmedSuggestions,
    isFirstOptionOrEnabled,
    showRadio: isFirstOptionOrEnabled && hasConfirmedSuggestions,
    label: isAddressFromUser
      ? 'Address you entered:'
      : `Suggested address${confirmedSuggestions.length > 1 ? 'es' : ''}:`,
  };
};

// Headings shift up two levels under the minimal header, where the page has
// fewer ancestor headings to sit beneath.
export const getDynamicHeaderTag = base =>
  `h${isMinimalHeaderPath() ? base - 2 : base}`;

export const getRadioLabelHeaderLevel = () => (isMinimalHeaderPath() ? 3 : 5);
